import React, { useState, useEffect } from "react";
import { useParams, useSearchParams, useLocation } from "react-router-dom";
import ImageViewer from "./DicomViewer";
import PdfViewer from "./PdfViewer";
import PhotoViewer from "./PhotoViewer";
import { documentViewerService } from "../../services/DocumentViewerService";
import { DocumentFormat } from "../../constants";

const DocumentViewerPage = () => {
  useEffect(() => {
    const savedTheme = localStorage.getItem("app-theme") || "teal";
    document.documentElement.setAttribute("data-theme", savedTheme);
  }, []);

  const { documentId } = useParams<{ documentId: string }>();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const filePath = location.state?.filePath;

  // Παίρνουμε το fileName απευθείας από το URL (query parameter)
  const fileName = decodeURIComponent(
    searchParams.get("fileName") || `Έγγραφο #${documentId}`,
  );
  const formatParam = searchParams.get("format");

  const [fileType, setFileType] = useState<
    "DICOM" | "PDF" | "IMAGE" | "WORD" | null
  >(null);

  const [fileUrl, setFileUrl] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>("");

  const resolveFileType = (formatNumber: number) => {
    switch (formatNumber) {
      case DocumentFormat.DICOM:
        setFileType("DICOM");
        break;
      case DocumentFormat.PDF:
        setFileType("PDF");
        break;
      case DocumentFormat.IMAGE:
        setFileType("IMAGE");
        break;
      default:
        setFileType("PDF");
        break;
    }
  };

  useEffect(() => {
    let currentUrl = "";

    const loadDocument = async () => {
      if (!filePath) {
        setLoading(false);
        setError("Η εγγραφή αυτή δεν περιέχει συνημμένο αρχείο.");
        return;
      }
      try {
        setLoading(true);
        setError("");

        if (formatParam !== null) {
          resolveFileType(Number(formatParam));
        } else {
          resolveFileType(DocumentFormat.DICOM);
        }

        const blob = await documentViewerService.getDocumentBlob(
          Number(documentId),
        );

        currentUrl = URL.createObjectURL(blob);
        setFileUrl(currentUrl);
      } catch (err) {
        console.error("Σφάλμα φόρτωσης εγγράφου:", err);
        setError("Δεν ήταν δυνατή η φόρτωση του αρχείου.");
      } finally {
        setLoading(false);
      }
    };

    if (documentId) {
      loadDocument();
    }

    return () => {
      if (currentUrl) {
        URL.revokeObjectURL(currentUrl);
      }
    };
  }, [documentId, formatParam]);

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-gray-900 text-white">
        Φόρτωση εξέτασης...
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-screen flex items-center justify-center bg-gray-900 text-red-400">
        {error}
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col">
      <header className="p-4 border-b bg-white flex justify-between items-center">
        {/* Εμφανίζει κανονικά το όνομα αρχείου πάνω-πάνω */}
        <h1 className="text-xl font-bold">Προβολή Εξέτασης: {fileName}</h1>

        <div className="space-x-2">
          <button
            onClick={() => setFileType("DICOM")}
            className={`px-3 py-1 rounded ${
              fileType === "DICOM" ? "bg-primary text-white" : "bg-gray-200"
            }`}
          >
            DICOM
          </button>

          <button
            onClick={() => setFileType("PDF")}
            className={`px-3 py-1 rounded ${
              fileType === "PDF" ? "bg-primary text-white" : "bg-gray-200"
            }`}
          >
            PDF
          </button>

          <button
            onClick={() => setFileType("IMAGE")}
            className={`px-3 py-1 rounded ${
              fileType === "IMAGE" ? "bg-primary text-white" : "bg-gray-200"
            }`}
          >
            Εικόνα
          </button>
        </div>
      </header>

      <main className="flex-1 bg-gray-900 overflow-hidden flex items-center justify-center">
        {fileType === "DICOM" && fileUrl && <ImageViewer dicomUrl={fileUrl} />}
        {fileType === "PDF" && fileUrl && <PdfViewer pdfUrl={fileUrl} />}
        {fileType === "IMAGE" && fileUrl && <PhotoViewer photoUrl={fileUrl} />}
      </main>
    </div>
  );
};

export default DocumentViewerPage;
